/**
 * 日月天体(方案 C1)。
 *
 * 补的是原实现的一个静默缺口:SkyState 定义了 `celestial: 'sun'|'moon'|'none'`,
 * 但全项目没有任何一处消费它(grep `celestial.` = 0 命中),Starfield 只画了星星。
 * 后果:天空上半部 60% 长期是一片纯底色,构图没有视觉锚点——这是"画面空"的主因之一。
 *
 * 遵守:
 *  - §A2.3 发光体法则:亮核 + 光晕L1(×2.5, α.35) + 光晕L2(×5, α.12),add 混合,呼吸 α
 *  - §A2.1 天空驱动一切:不持有写死颜色,月相暗部取 sky.state.top(即"透出背后的天")
 *  - §C1 太阳不画实体圆盘,只给 divine 大光晕,避免卡通感
 */
import { Container, Graphics, Sprite } from 'pixi.js';
import { Stage, LW, LH } from '../engine/Stage';
import { sky } from '../engine/SkyRenderer';
import { TEX } from '../engine/Tex';
import { P, mix } from '../engine/Palette';
import { clock } from '../engine/Ticker';

const R_MOON = 46;          // §C1 月半径
const R_SUN = 30;           // 日核半径(软边盘)
const R_SUN_HALO = 150;     // 太阳光晕半径
const GLOW_TEX_R = 128;     // TEX.glow() 是 256px,半径 128
const CORE_TEX_R = 64;      // TEX.core() 是 128px,半径 64

/** 天穹弧线:t=0 东升 → t=0.5 中天 → t=1 西落 */
function arcAt(t: number): { x: number; y: number } {
  return {
    x: LW * (0.12 + 0.76 * t),
    y: LH * 0.56 - Math.sin(t * Math.PI) * LH * 0.42,
  };
}

/** 月龄 0~1(0=新月, 0.5=满月)。基准新月:2000-01-06 18:14 UTC。 */
function lunarPhase(): number {
  const SYNODIC = 29.530588853 * 86400000;
  const epoch = Date.UTC(2000, 0, 6, 18, 14);
  return (((Date.now() - epoch) % SYNODIC) + SYNODIC) % SYNODIC / SYNODIC;
}

export class Celestial {
  private moonGrp = new Container();
  private sunGrp = new Container();
  private disk = new Graphics();
  private shadow = new Graphics();
  private shadowMask = new Graphics(); // 把月相暗部裁在月盘内,防止盖到天空上
  private mHalo1: Sprite;
  private mHalo2: Sprite;
  private sHalo1: Sprite;
  private sHalo2: Sprite;
  private sCore!: Sprite;
  private t = 0;
  private mA = 0;   // 月透明度(缓入缓出,避免 celestial 硬切)
  private sA = 0;
  private phM = Math.random() * 6.28;
  private phS = Math.random() * 6.28;
  private perM = 3 + Math.random() * 2;
  private perS = 3 + Math.random() * 2;

  constructor(stage: Stage) {
    const glow = (r: number, alpha: number, tint: number) => {
      const s = new Sprite(TEX.glow());
      s.anchor.set(0.5);
      s.blendMode = 'add';
      s.tint = tint;
      s.alpha = alpha;
      s.scale.set(r / GLOW_TEX_R);
      return s;
    };

    // ── 月:光晕L2 → 光晕L1 → 亮核圆 → 月相暗部 ──
    this.mHalo2 = glow(R_MOON * 5, 0.12, P.moon);
    this.mHalo1 = glow(R_MOON * 2.5, 0.35, P.moon);
    this.shadowMask.circle(0, 0, R_MOON).fill(0xffffff);
    this.shadow.mask = this.shadowMask;
    this.moonGrp.addChild(this.mHalo2, this.mHalo1, this.disk, this.shadowMask, this.shadow);

    // ── 日:软边日核 + 双光晕 ──
    // 上一版按 §C1「不画实体圆盘,只给大光晕」执行,实测是败笔:700px 纯弥散
    // 光晕没有任何亮度收拢点,挂在天顶就是一块来历不明的"大光斑"。天体要被
    // 读成天体,必须有一个核。用 TEX.core(55% 处才开始衰减)做软边日盘——
    // 有实体感但无硬描边,不违背 §C1 反卡通的本意。同时:长暮世界(DAY 已
    // 砍,白昼即 LONGDUSK)的太阳应当低垂偏暖,故整体缩小、色温从天顶白金
    // 改为琥珀,光晕半径 220→150。
    this.sCore = new Sprite(TEX.core());
    this.sCore.anchor.set(0.5); this.sCore.blendMode = 'add';
    this.sCore.tint = 0xffe2b0; this.sCore.scale.set(R_SUN / CORE_TEX_R);
    this.sHalo2 = glow(R_SUN_HALO * 1.5, 0.16, 0xffb974);
    this.sHalo1 = glow(R_SUN_HALO * 0.7, 0.26, 0xffd9a0);
    this.sunGrp.addChild(this.sHalo2, this.sHalo1, this.sCore);

    // celestial 层(z=1),排在 Starfield 之前,让星星画在月晕之上
    stage.layers.celestial.addChildAt(this.moonGrp, 0);
    stage.layers.celestial.addChildAt(this.sunGrp, 0);
  }

  update(dt: number) {
    this.t += dt;
    const h = clock.hour();
    const cel = sky.state.celestial;

    // 目标透明度:celestial 由 SkyState 决定,这里做 1.5s 缓入缓出防硬切
    const rate = dt / 1.5;
    this.mA += (Math.min(1, (cel === 'moon' ? 1 : 0)) - this.mA) * Math.min(1, rate * 4);
    this.sA += (Math.min(1, (cel === 'sun' ? 1 : 0)) - this.sA) * Math.min(1, rate * 4);

    // ── 月:18:00 东升 → 06:00 西落 ──
    if (this.mA > 0.003) {
      this.moonGrp.visible = true;
      const tm = (((h - 18 + 24) % 24) / 12);
      const p = arcAt(tm);
      this.moonGrp.position.set(p.x, p.y);
      const breath = 0.9 + 0.1 * Math.sin((this.t / this.perM) * Math.PI * 2 + this.phM);
      this.moonGrp.alpha = this.mA;
      this.mHalo1.alpha = 0.35 * breath;
      this.mHalo2.alpha = 0.12 * breath;
      this.drawMoon();
    } else {
      this.moonGrp.visible = false;
    }

    // ── 日:06:00 东升 → 18:00 西落 ──
    // 不走 arcAt 的高弧(那是月亮的):长暮世界的太阳永远贴着地平线巡行,
    // 最高只到 0.32LH——低垂本身就是"永恒黄昏"的叙事。
    if (this.sA > 0.003) {
      this.sunGrp.visible = true;
      const ts = Math.max(0, Math.min(1, (h - 6) / 12));
      this.sunGrp.position.set(
        LW * (0.12 + 0.76 * ts),
        LH * 0.52 - Math.sin(ts * Math.PI) * LH * 0.20,
      );
      const breath = 0.9 + 0.1 * Math.sin((this.t / this.perS) * Math.PI * 2 + this.phS);
      // 落日增强:ts>0.7(约 14:24 后)起,太阳渐大渐红渐亮——低垂的淡太阳在
      // 亮橙暮色上会同色系隐身(用户实测"太阳没了"的体感根源);真实大气里
      // 落日也正是最大最红的时刻,物理与可读性在此同向。
      const low = Math.max(0, (ts - 0.7) / 0.3);
      this.sunGrp.scale.set(1 + low * 0.35);
      this.sCore.tint = mix(0xffe2b0, 0xff8a50, low);
      this.sHalo1.tint = mix(0xffd9a0, 0xff9e6b, low);
      this.sunGrp.alpha = this.sA;
      this.sHalo2.alpha = (0.16 + low * 0.1) * breath;
      this.sHalo1.alpha = (0.26 + low * 0.16) * breath;
      this.sCore.alpha = 0.9;
    } else {
      this.sunGrp.visible = false;
    }
  }

  /** 月盘 + 月相暗部。暗部色取 sky.state.top —— 等价于"透出背后的天",不写死颜色。 */
  private drawMoon() {
    const ph = lunarPhase();
    this.disk.clear().circle(0, 0, R_MOON).fill(P.moon);

    // 暗部 = 一个与月盘等大、按月龄横向偏移的圆;偏移 ±2R 时全亮(满月),0 时全暗(新月)
    const k = Math.cos(ph * Math.PI * 2);      // +1 新月 → -1 满月
    const off = k * R_MOON * 2.05;
    this.shadow.clear().circle(off, 0, R_MOON * 1.02).fill(sky.state.top);
    this.shadow.visible = Math.abs(off) < R_MOON * 2.02; // 满月时暗部完全移出,不必画
  }
}
